import {
	Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { Chart } from 'src/app/shared/utils/chart-setup';
import { AiUsageTrackingService, AdminFilters } from 'src/app/core/services/ai/ai-usage-tracking.service';
import {
	AiUsageAdminSummary, AiUsageAdminLog, AiUsageAdminLogPage,
	AiUsageAdminStats, AiUsageFilterOptions,
	OpenAIBillingResponse, ReconciliationResponse,
	OpenAIBillingSyncResponse, OpenAIBillingSyncStatus
} from 'src/app/core/models/ai/ai-usage.model';

@Component({
	selector: 'app-modal-ai-usage-stats',
	templateUrl: './modal-ai-usage-stats.component.html',
	styleUrls: ['./modal-ai-usage-stats.component.scss'],
	standalone: false
})
export class ModalAiUsageStatsComponent implements OnInit, OnDestroy, AfterViewInit {

	private destroy$ = new Subject<void>();

	// Data
	stats: AiUsageAdminStats | null = null;
	summaries: AiUsageAdminSummary[] = [];
	filteredSummaries: AiUsageAdminSummary[] = [];
	logs: AiUsageAdminLog[] = [];
	filterOptions: AiUsageFilterOptions = { features: [], models: [], providers: [] };

	// Pagination
	logPage = 0;
	logSize = 15;
	logTotalElements = 0;
	logTotalPages = 0;

	// Filters
	filterUser: string = '';
	filterFeature: string = '';
	filterModel: string = '';
	filterProvider: string = '';
	filterStartDate: string = '';
	filterEndDate: string = '';

	// Sort
	logSortBy = 'callDate';
	logSortDir = 'desc';
	summarySortBy = 'totalTokens';
	summarySortDir = 'desc';

	// Loading
	loadingStats = true;
	loadingSummaries = true;
	loadingLogs = true;
	loadingOpenAi = false;
	loadingReconciliation = false;
	syncing = false;

	// Active tab
	activeTab: 'charts' | 'summaries' | 'logs' | 'openai' | 'reconciliation' = 'charts';

	// Fase 4 — OpenAI billing
	openAiData: OpenAIBillingResponse | null = null;
	reconciliation: ReconciliationResponse | null = null;
	syncHistory: OpenAIBillingSyncStatus[] = [];
	lastSyncMessage: string | null = null;
	lastSyncStatus: 'STARTED' | 'COMPLETED' | 'FAILED' | 'SKIPPED' | null = null;

	// Charts
	@ViewChild('chartFeature') chartFeatureRef!: ElementRef<HTMLCanvasElement>;
	@ViewChild('chartModel') chartModelRef!: ElementRef<HTMLCanvasElement>;
	@ViewChild('chartTopUsers') chartTopUsersRef!: ElementRef<HTMLCanvasElement>;
	@ViewChild('chartTimeSeries') chartTimeSeriesRef!: ElementRef<HTMLCanvasElement>;

	private chartFeature: Chart | null = null;
	private chartModel: Chart | null = null;
	private chartTopUsers: Chart | null = null;
	private chartTimeSeries: Chart | null = null;

	private chartColors = [
		'#002554', '#00c4b3', '#3366ff', '#ff6b35', '#7c4dff',
		'#00bcd4', '#ff5252', '#66bb6a', '#ffa726', '#ab47bc',
		'#26a69a', '#ef5350', '#42a5f5', '#ffca28', '#8d6e63'
	];

	constructor(
		public activeModal: NgbActiveModal,
		private aiUsageService: AiUsageTrackingService
	) {}

	ngOnInit(): void {
		this.loadFilterOptions();
		this.loadStats();
		this.loadSummaries();
		this.loadLogs();
	}

	ngAfterViewInit(): void {
		// Charts are built after stats load
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.destroyCharts();
	}

	closeModal(): void {
		this.activeModal.close();
	}

	switchTab(tab: 'charts' | 'summaries' | 'logs' | 'openai' | 'reconciliation'): void {
		this.activeTab = tab;
		if (tab === 'charts' && this.stats) {
			setTimeout(() => this.buildCharts(), 100);
		}
		if (tab === 'openai' && !this.openAiData) {
			this.loadOpenAiData();
			this.loadSyncHistory();
		}
		if (tab === 'reconciliation' && !this.reconciliation) {
			this.loadReconciliation();
		}
	}

	// ── Fase 4 — OpenAI billing data loading ────────────────────────────

	loadOpenAiData(): void {
		this.loadingOpenAi = true;
		this.aiUsageService.getOpenAICosts(
			this.filterStartDate || undefined,
			this.filterEndDate || undefined
		).pipe(
			takeUntil(this.destroy$),
			finalize(() => this.loadingOpenAi = false)
		).subscribe({
			next: data => this.openAiData = data
		});
	}

	loadReconciliation(): void {
		this.loadingReconciliation = true;
		this.aiUsageService.getReconciliation(
			this.filterStartDate || undefined,
			this.filterEndDate || undefined
		).pipe(
			takeUntil(this.destroy$),
			finalize(() => this.loadingReconciliation = false)
		).subscribe({
			next: data => this.reconciliation = data
		});
	}

	loadSyncHistory(): void {
		this.aiUsageService.getOpenAISyncStatus(10)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: data => this.syncHistory = data
			});
	}

	syncOpenAiCosts(): void {
		if (this.syncing) { return; }
		this.syncing = true;
		this.lastSyncMessage = null;
		this.lastSyncStatus = null;
		this.aiUsageService.syncOpenAICosts(
			this.filterStartDate || undefined,
			this.filterEndDate || undefined
		).pipe(
			takeUntil(this.destroy$),
			finalize(() => this.syncing = false)
		).subscribe({
			next: (result: OpenAIBillingSyncResponse) => {
				this.lastSyncStatus = result.status;
				this.lastSyncMessage = result.message;
				this.loadOpenAiData();
				this.loadReconciliation();
				this.loadSyncHistory();
			},
			error: err => {
				this.lastSyncStatus = 'FAILED';
				this.lastSyncMessage = err?.error?.message || 'Error al lanzar la sincronización';
			}
		});
	}

	// ── Data loading ─────────────────────────────────────────────────────

	loadFilterOptions(): void {
		this.aiUsageService.getFilterOptions()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: opts => this.filterOptions = opts,
				error: () => {}
			});
	}

	/**
	 * Construye los filtros que entiende el back. Si el campo "filterUser"
	 * es puramente numérico, se envía como idUser al back; si es texto se
	 * deja sin enviar y el filtrado se aplica luego en cliente sobre el
	 * nombre/email del usuario.
	 */
	private buildBackendFilters(): AdminFilters {
		return {
			idUser: this.isNumericFilterUser() ? Number(this.filterUser) : undefined,
			feature: this.filterFeature || undefined,
			model: this.filterModel || undefined,
			provider: this.filterProvider || undefined,
			startDate: this.filterStartDate || undefined,
			endDate: this.filterEndDate || undefined
		};
	}

	private isNumericFilterUser(): boolean {
		return !!this.filterUser && /^\d+$/.test(this.filterUser.trim());
	}

	loadStats(): void {
		this.loadingStats = true;
		this.aiUsageService.getAdminStats(this.buildBackendFilters()).pipe(
			takeUntil(this.destroy$),
			finalize(() => this.loadingStats = false)
		).subscribe({
			next: data => {
				this.stats = data;
				setTimeout(() => this.buildCharts(), 100);
			}
		});
	}

	loadSummaries(): void {
		this.loadingSummaries = true;
		this.aiUsageService.getAdminSummaries(this.buildBackendFilters())
			.pipe(
				takeUntil(this.destroy$),
				finalize(() => this.loadingSummaries = false)
			)
			.subscribe({
				next: data => {
					this.summaries = data;
					this.applySummaryFilters();
				}
			});
	}

	loadLogs(): void {
		this.loadingLogs = true;
		this.aiUsageService.getAdminLogs({
			...this.buildBackendFilters(),
			page: this.logPage,
			size: this.logSize,
			sortBy: this.logSortBy,
			sortDir: this.logSortDir
		}).pipe(
			takeUntil(this.destroy$),
			finalize(() => this.loadingLogs = false)
		).subscribe({
			next: (page: AiUsageAdminLogPage) => {
				this.logs = page.content;
				this.logTotalElements = page.totalElements;
				this.logTotalPages = page.totalPages;
			}
		});
	}

	// ── Filters ──────────────────────────────────────────────────────────

	applyFilters(): void {
		this.logPage = 0;
		this.loadStats();
		this.loadSummaries();
		this.loadLogs();
		if (this.activeTab === 'openai') { this.loadOpenAiData(); }
		if (this.activeTab === 'reconciliation') { this.loadReconciliation(); }
	}

	clearFilters(): void {
		this.filterUser = '';
		this.filterFeature = '';
		this.filterModel = '';
		this.filterProvider = '';
		this.filterStartDate = '';
		this.filterEndDate = '';
		this.applyFilters();
	}

	/**
	 * Filtra en cliente solo cuando filterUser es texto (nombre/email);
	 * cuando es numérico el back ya filtró por idUser.
	 */
	applySummaryFilters(): void {
		let data = [...this.summaries];
		if (this.filterUser && !this.isNumericFilterUser()) {
			const search = this.filterUser.toLowerCase();
			data = data.filter(s =>
				s.firstName?.toLowerCase().includes(search) ||
				s.surname?.toLowerCase().includes(search) ||
				s.mail?.toLowerCase().includes(search)
			);
		}
		this.sortSummaries(data);
	}

	// ── Sorting ──────────────────────────────────────────────────────────

	sortSummariesBy(column: string): void {
		if (this.summarySortBy === column) {
			this.summarySortDir = this.summarySortDir === 'asc' ? 'desc' : 'asc';
		} else {
			this.summarySortBy = column;
			this.summarySortDir = 'desc';
		}
		this.sortSummaries(this.filteredSummaries);
	}

	private sortSummaries(data: AiUsageAdminSummary[]): void {
		const dir = this.summarySortDir === 'asc' ? 1 : -1;
		const key = this.summarySortBy;
		data.sort((a: any, b: any) => {
			const va = a[key] ?? '';
			const vb = b[key] ?? '';
			if (typeof va === 'string') return va.localeCompare(vb) * dir;
			return (va - vb) * dir;
		});
		this.filteredSummaries = data;
	}

	sortLogsBy(column: string): void {
		if (this.logSortBy === column) {
			this.logSortDir = this.logSortDir === 'asc' ? 'desc' : 'asc';
		} else {
			this.logSortBy = column;
			this.logSortDir = 'desc';
		}
		this.logPage = 0;
		this.loadLogs();
	}

	getSortIcon(column: string, currentSort: string, currentDir: string): string {
		if (currentSort !== column) return '↕';
		return currentDir === 'asc' ? '↑' : '↓';
	}

	// ── Pagination ───────────────────────────────────────────────────────

	goToPage(page: number): void {
		if (page < 0 || page >= this.logTotalPages) return;
		this.logPage = page;
		this.loadLogs();
	}

	getPageNumbers(): number[] {
		const pages: number[] = [];
		const start = Math.max(0, this.logPage - 2);
		const end = Math.min(this.logTotalPages - 1, this.logPage + 2);
		for (let i = start; i <= end; i++) {
			pages.push(i);
		}
		return pages;
	}

	// ── Charts ───────────────────────────────────────────────────────────

	private destroyCharts(): void {
		this.chartFeature?.destroy();
		this.chartModel?.destroy();
		this.chartTopUsers?.destroy();
		this.chartTimeSeries?.destroy();
		this.chartFeature = null;
		this.chartModel = null;
		this.chartTopUsers = null;
		this.chartTimeSeries = null;
	}

	private buildCharts(): void {
		if (!this.stats) return;
		this.destroyCharts();
		this.buildFeatureChart();
		this.buildModelChart();
		this.buildTopUsersChart();
		this.buildTimeSeriesChart();
	}

	private buildFeatureChart(): void {
		const el = this.chartFeatureRef?.nativeElement;
		if (!el || !this.stats) return;
		const data = this.stats.byFeature;
		this.chartFeature = new Chart(el, {
			type: 'bar',
			data: {
				labels: data.map(d => d.label),
				datasets: [{
					label: 'Tokens totales',
					data: data.map(d => d.totalTokens),
					backgroundColor: this.chartColors.slice(0, data.length)
				}]
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					legend: { display: false },
					title: { display: true, text: 'Consumo por Feature' }
				},
				scales: {
					x: { ticks: { maxRotation: 45, font: { size: 10 } } },
					y: { beginAtZero: true }
				}
			}
		});
	}

	private buildModelChart(): void {
		const el = this.chartModelRef?.nativeElement;
		if (!el || !this.stats) return;
		const data = this.stats.byModel;
		this.chartModel = new Chart(el, {
			type: 'bar',
			data: {
				labels: data.map(d => d.label),
				datasets: [{
					label: 'Tokens totales',
					data: data.map(d => d.totalTokens),
					backgroundColor: this.chartColors.slice(0, data.length)
				}]
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					legend: { display: false },
					title: { display: true, text: 'Consumo por Modelo' }
				},
				scales: {
					x: { ticks: { maxRotation: 45, font: { size: 10 } } },
					y: { beginAtZero: true }
				}
			}
		});
	}

	private buildTopUsersChart(): void {
		const el = this.chartTopUsersRef?.nativeElement;
		if (!el || !this.stats) return;
		const data = this.stats.topUsers;
		this.chartTopUsers = new Chart(el, {
			type: 'bar',
			data: {
				labels: data.map(d => `${d.firstName} ${d.surname}`.trim()),
				datasets: [{
					label: 'Tokens totales',
					data: data.map(d => d.totalTokens),
					backgroundColor: '#002554'
				}]
			},
			options: {
				indexAxis: 'y',
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					legend: { display: false },
					title: { display: true, text: 'Top 10 Usuarios' }
				},
				scales: {
					x: { beginAtZero: true }
				}
			}
		});
	}

	private buildTimeSeriesChart(): void {
		const el = this.chartTimeSeriesRef?.nativeElement;
		if (!el || !this.stats) return;
		const data = this.stats.timeSeries;
		this.chartTimeSeries = new Chart(el, {
			type: 'line',
			data: {
				labels: data.map(d => d.date),
				datasets: [{
					label: 'Tokens totales',
					data: data.map(d => d.totalTokens),
					borderColor: '#00c4b3',
					backgroundColor: 'rgba(0, 196, 179, 0.15)',
					fill: true,
					tension: 0.3,
					pointRadius: 2
				}]
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					legend: { display: false },
					title: { display: true, text: 'Evolución Temporal' }
				},
				scales: {
					x: { ticks: { maxRotation: 45, font: { size: 10 } } },
					y: { beginAtZero: true }
				}
			}
		});
	}

	// ── Helpers ───────────────────────────────────────────────────────────

	formatNumber(value: number | null | undefined): string {
		if (value == null) return '0';
		return value.toLocaleString('es-ES');
	}

	formatUsd(value: number | string | null | undefined): string {
		if (value == null || value === '') return '—';
		const num = typeof value === 'string' ? Number(value) : value;
		if (!isFinite(num) || num === 0) return '$0.00';
		if (num < 0.01) return '<$0.01';
		return num.toLocaleString('en-US', {
			style: 'currency',
			currency: 'USD',
			minimumFractionDigits: 2,
			maximumFractionDigits: 4
		});
	}

	formatDate(dateStr: string): string {
		if (!dateStr) return '-';
		const d = new Date(dateStr);
		return d.toLocaleDateString('es-ES', {
			day: '2-digit', month: '2-digit', year: 'numeric',
			hour: '2-digit', minute: '2-digit'
		});
	}
}
